import { useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { OverlayScrollbarsComponent, OverlayScrollbarsComponentRef } from 'overlayscrollbars-react';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  Button,
  Divider,
  Badge,
  Stack,
  SwipeableDrawer,
  Slide,
  Alert,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import TuneIcon from '@mui/icons-material/Tune';
import CloseIcon from '@mui/icons-material/Close';
import Add from '@mui/icons-material/Add';

import {
  ClientResponseDto,
  TeamResponseDto,
  UserResponseDto,
  WorkflowResponseDto,
  WorkflowStepResponseDto,
} from '@bandido/shared/dtos';

import { useGetAllCasesInfinity } from '@frontend/features/cases/queries';

import { router } from '@frontend/router/router';
import { routes } from '@frontend/router/routes';
import { useTwilio } from '@frontend/features/communications/TwilioContextProvider/provider';
import { useSnackBar } from '@frontend/features/common/components/SnackBarContext';
import { useDebouncedState } from '@frontend/features/common/hooks/useDebouncedState';
import { useIntersectionObserver } from '@frontend/features/common/hooks/useIntersectionObserver';
import { useScrollHandling } from '@frontend/features/common/hooks/useScrollHandling';
import { CONFIG } from '@frontend/config';

import { useAssignCallToCase } from '../../queries';
import {
  drawerRootStyles,
  drawerPaperStyles,
  titleStyles,
  dividerStyles,
  contentStyles,
  searchContainerStyles,
  searchInputStyles,
  filterButtonStyles,
  badgeStyles,
} from './styles';
import LoadingState from './components/LoadingState';
import { CaseCard } from './components/CaseCard';
import { CaseFiltersForm } from './components/CaseFiltersForm';
import CreateCaseCard from './components/CreateCaseCard';

export interface FormState {
  teams: TeamResponseDto['id'][];
  agents: UserResponseDto['id'][];
  steps: WorkflowStepResponseDto['id'][];
  workflows: WorkflowResponseDto['id'][];
  clients: ClientResponseDto['id'][];
}
interface CallAssignmentDrawerProps {
  open: boolean;
  onClose: () => void;
}

export const CallAssignmentDrawer = ({ open, onClose }: CallAssignmentDrawerProps) => {
  const { t } = useTranslation();
  const { callData } = useTwilio();
  const { addToast } = useSnackBar();
  const { scrollDirection, handleScroll } = useScrollHandling();

  const [search, setSearch] = useDebouncedState('', 500);
  const [isCreatingCase, setIsCreatingCase] = useState(false);
  const [seletedCase, setSeletedCase] = useState<number | null>(null);
  const [isFiltersFormOpen, setIsFiltersFormOpen] = useState(false);

  const bottomRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<OverlayScrollbarsComponentRef<'div'>>(null);

  const methods = useForm<FormState>({
    defaultValues: {
      teams: [],
      agents: [],
      steps: [],
      workflows: [],
      clients: [],
    },
  });

  const selectedTeams = methods.watch('teams');
  const selectedWorflows = methods.watch('workflows');
  const selectedSteps = methods.watch('steps');
  const selectedAgents = methods.watch('agents');
  const selectedClients = methods.watch('clients');

  const scrollInstance = useMemo(() => scrollRef.current?.osInstance()?.elements()?.viewport, [scrollRef.current]);

  const { data, isLoading, isFetchingNextPage, fetchNextPage, hasNextPage } = useGetAllCasesInfinity({
    search,
    agencyId: callData?.agencyId ?? CONFIG.AGENCY_ID,
    teamIds: selectedTeams,
    workflowIds: selectedWorflows,
    stepIds: selectedSteps,
    agentIds: selectedAgents,
    clientIds: selectedClients,
  });

  const casesList = data?.pages?.map((page) => page?.data.items).flat();

  const callId = String(callData?.callId);

  const mutation = useAssignCallToCase(callId, {
    onSettled: () => {
      onClose();
      addToast({
        message: t('calls.assignment.success'),
        color: 'success',
      });
    },
    onError: () => {
      addToast({
        message: t('calls.assignment.error'),
        color: 'error',
      });
    },
  });

  const handleToggleFiltersForm = () => setIsFiltersFormOpen((prev) => !prev);

  const handleCallLink = (caseId: number) => {
    setSeletedCase(caseId);
    mutation.mutate({
      caseId,
    });
  };

  useEffect(() => {
    if (callData?.calleeInfo?.id) {
      methods.setValue('clients', [callData.calleeInfo.id]);
    }
  }, [callData?.calleeInfo?.id, methods.setValue]);

  useEffect(() => {
    if (seletedCase) {
      router.navigate(routes.dashboard.caseView.replace(':id', String(seletedCase)));
    }
  }, [seletedCase]);

  useIntersectionObserver({
    targetRef: bottomRef,
    onIntersect: () => {
      fetchNextPage();

      if (scrollInstance) {
        const maxScrollHeight = scrollInstance.scrollHeight - scrollInstance.clientHeight;
        scrollInstance.scrollTop = maxScrollHeight;
      }
    },
    enabled: Boolean(!isFetchingNextPage && scrollDirection === 'down' && hasNextPage),
  });

  const FilterIcon = isFiltersFormOpen ? CloseIcon : TuneIcon;
  const filtersCount =
    selectedTeams.length +
    selectedWorflows.length +
    selectedSteps.length +
    selectedAgents.length +
    selectedClients.length;

  return (
    <SwipeableDrawer
      anchor="right"
      open={open}
      onOpen={() => {}}
      hideBackdrop
      sx={drawerRootStyles}
      onClose={onClose}
      ModalProps={{
        disableAutoFocus: true,
      }}
      PaperProps={{
        elevation: 8,
        sx: drawerPaperStyles,
      }}
    >
      <OverlayScrollbarsComponent
        ref={scrollRef}
        events={{ scroll: handleScroll }}
        options={{
          scrollbars: {
            autoHide: 'leave',
            theme: 'os-theme-light',
          },
        }}
        style={{
          overflow: 'hidden',
          height: '100vh',
        }}
      >
        <Box position="relative" flex={1}>
          <Typography sx={titleStyles} variant="subtitle1">
            {t('calls.assignment.title')}
          </Typography>
          <Divider sx={dividerStyles} />
          <Slide direction={!isCreatingCase ? 'right' : 'left'} in={!isCreatingCase} mountOnEnter unmountOnExit>
            <Box display={!isCreatingCase ? 'block' : 'none'} height="100%" width="100%">
              <Stack sx={contentStyles}>
                <Box sx={searchContainerStyles}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder={t('calls.assignment.search')}
                    InputProps={{
                      sx: searchInputStyles,
                      size: 'small',
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ color: (theme) => theme.palette.common.white }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <IconButton onClick={handleToggleFiltersForm} sx={filterButtonStyles}>
                    <Badge sx={badgeStyles} badgeContent={isFiltersFormOpen ? 0 : filtersCount} color="error">
                      <FilterIcon sx={{ color: 'text.primary' }} />
                    </Badge>
                  </IconButton>
                </Box>
                <FormProvider {...methods}>
                  {isFiltersFormOpen && <CaseFiltersForm agencyId={callData?.agencyId ?? CONFIG.AGENCY_ID} />}{' '}
                </FormProvider>
                <Button
                  onClick={() => setIsCreatingCase((prev) => !prev)}
                  fullWidth
                  color="secondary"
                  variant="outlined"
                  startIcon={<Add />}
                  sx={{
                    width: 'fit-content',
                  }}
                >
                  {t('calls.assignment.newCase')}
                </Button>
                <Box>
                  {isLoading ? (
                    <LoadingState />
                  ) : (
                    casesList?.map((caseData, index) => (
                      <CaseCard
                        isLoading={mutation.isLoading}
                        key={caseData.id}
                        caseData={caseData}
                        index={index}
                        totalCases={casesList.length}
                        onCallLink={handleCallLink}
                      />
                    ))
                  )}
                </Box>
                <Box sx={{ height: 50, width: '100%' }} ref={bottomRef} />
              </Stack>
            </Box>
          </Slide>
          <Slide direction={isCreatingCase ? 'left' : 'right'} in={isCreatingCase} mountOnEnter unmountOnExit>
            <Box display={isCreatingCase ? 'block' : 'none'} height="100%" width="100%">
              <Stack sx={{ ...contentStyles, gap: 3 }}>
                <Alert sx={{ color: 'primary.contrastText' }} severity="warning" variant="outlined" color="warning">
                  {t('calls.assignment.create.warning')}
                </Alert>
                <Typography>{t('calls.assignment.create.title')}</Typography>
                <CreateCaseCard onCancel={() => setIsCreatingCase(false)} onClose={onClose} />
              </Stack>
            </Box>
          </Slide>
        </Box>
      </OverlayScrollbarsComponent>
    </SwipeableDrawer>
  );
};
